import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Shared chrome for /sign-in, /sign-up, password reset, and email
 * verification pages. Centered card on top of the same animated
 * grid used elsewhere on the marketing site so auth pages don't
 * feel detached from the rest of the brand.
 */
interface AuthCardProps {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}

export function AuthCard({ title, subtitle, children, footer }: AuthCardProps) {
  return (
    <section className="relative flex min-h-[100vh] items-center justify-center px-4 pt-24 pb-16">
      <div
        className="pointer-events-none absolute inset-0 -z-10 marketing-grid-bg opacity-30"
        aria-hidden="true"
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          'relative w-full max-w-md rounded-2xl border border-border bg-card/80 backdrop-blur-md',
          'p-8 shadow-xl shadow-primary/5',
        )}
      >
        <div className="text-center">
          <h1 className="text-balance text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
          {subtitle ? (
            <p className="mt-2 text-pretty text-sm text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>

        <div className="mt-8">{children}</div>

        {footer ? (
          <div className="mt-6 border-t border-border pt-6 text-center text-sm text-muted-foreground">
            {footer}
          </div>
        ) : null}
      </motion.div>
    </section>
  )
}
